import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";

import { Input } from "@shared/components/ui/input";
import { Label } from "@shared/components/ui/label";
import { Textarea } from "@shared/components/ui/textarea";

import {
  HandCoins,
  CheckCircle2,
  Building2,
  CreditCard,
  Download,
  FileText,
  Loader2,
} from "lucide-react";
import { Button } from "@shared/components/ui/button";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateInvoiceStatus } from "@features/invoices/services/invoiceService";
import { invoicePDFService } from "@features/invoices/services/invoicePDFService";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  INVOICE_QUERY_KEYS,
  INVOICE_STATUS_VALUES,
} from "@features/invoices/constants";

const InvoiceActions = ({
  invoiceId,
  invoice,
  clientInfo,
  showPDFActions = false,
  showMarkAsPaid = false,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentProof, setPaymentProof] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationFn: (data) => updateInvoiceStatus(invoiceId, data),
  });

  const handlePaid = () => {
    const formData = new FormData();
    formData.append("status", INVOICE_STATUS_VALUES.PAID);
    formData.append("note", paymentNote);
    if (paymentProof) {
      formData.append("paymentProof", paymentProof);
    }

    mutate(formData, {
      onSuccess: () => {
        toast.success(t("invoices:invoiceMarkedPaid"));
        queryClient.invalidateQueries({ queryKey: [INVOICE_QUERY_KEYS.ALL] });
      },
      onError: (e) => {
        toast.error(e.message);
      },
    });
  };

  const handleGeneratePDF = async () => {
    try {
      setIsGenerating(true);
      await invoicePDFService.generatePDF(invoiceId);
      toast.success(t("invoices.pdf.pdfGenerated"));
      queryClient.invalidateQueries({ queryKey: [INVOICE_QUERY_KEYS.ALL] });
    } catch (error) {
      toast.error(error.message || t("invoices.pdf.error.generation"));
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);
      await invoicePDFService.downloadPDF(
        invoiceId,
        `facture-${invoice?.ref || invoiceId}.pdf`
      );
      toast.success(t("invoices.pdf.pdfDownloaded"));
    } catch (error) {
      toast.error(error.message || t("invoices.pdf.error.download"));
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* PDF Actions - shown when showPDFActions is true */}
      {showPDFActions && (
        <>
          {/* Generate PDF Button */}
          <Button
            size="sm"
            variant={invoice?.pdfPath ? "outline" : "ghost"}
            onClick={handleGeneratePDF}
            disabled={isGenerating || isDownloading}
            className="flex items-center gap-1"
          >
            {isGenerating ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <FileText className="size-4" />
            )}
          </Button>

          {/* Download PDF Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDownloadPDF}
            disabled={!invoice?.pdfPath || isGenerating || isDownloading}
            className={`flex items-center gap-1 ${
              !invoice?.pdfPath ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isDownloading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Download className="size-4" />
            )}
          </Button>
        </>
      )}

      {/* Mark as Paid Dialog - only shown when showMarkAsPaid is true */}
      {showMarkAsPaid && (
        <Dialog>
          <DialogTrigger asChild>
            <Button
              disabled={isPending}
              variant="ghost"
              className="text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              <CheckCircle2 className="size-4 mx-1" />
              <span>{t("invoices.paid")}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HandCoins className="h-5 w-5 text-green-600" />
                {t("invoices.action")}
              </DialogTitle>
              <DialogDescription>{t("invoices.markAsPaid")}</DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Client Information Section */}
              <div className="space-y-4 p-4 rounded-lg border bg-muted/50">
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {t("invoices.clientInfo")}:
                  </span>
                </div>

                <div className="grid gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="client" className="text-sm font-medium">
                      {t("invoices.client")}
                    </Label>
                    <Input
                      id="client"
                      type="text"
                      value={clientInfo?.brandName || clientInfo?.name || ""}
                      disabled
                      className="bg-background"
                    />
                  </div>

                  {clientInfo?.bankAccount?.bankNumber && (
                    <div className="space-y-2">
                      <Label
                        htmlFor="bankAccount"
                        className="text-sm font-medium"
                      >
                        {t("invoices.bankAccount")}
                      </Label>
                      <div className="flex items-center gap-2">
                        <CreditCard className="h-4 w-4 text-muted-foreground" />
                        <Input
                          id="bankAccount"
                          type="text"
                          value={clientInfo.bankAccount.bankNumber}
                          disabled
                          className="bg-background"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Note Section */}
              <div className="space-y-2">
                <Label htmlFor="note" className="text-sm font-medium">
                  {t("common.note")} ({t("common.optional")})
                </Label>
                <Textarea
                  id="note"
                  placeholder={t("common.note") + " ..."}
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  rows={3}
                />
              </div>

              {/* Payment Proof Section */}
              <div className="space-y-2">
                <Label htmlFor="paymentProof" className="text-sm font-medium">
                  {t("invoices.paymentProof")} ({t("common.optional")})
                </Label>
                <Input
                  id="paymentProof"
                  type="file"
                  accept="image/*,.pdf"
                  onChange={(e) => setPaymentProof(e.target.files[0])}
                  className="cursor-pointer"
                />
                {paymentProof && (
                  <p className="text-xs text-muted-foreground">
                    {t("invoices.selectedFile")}: {paymentProof.name}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-2 sm:gap-0">
              <DialogClose asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="w-full sm:w-auto"
                >
                  {t("common.cancel")}
                </Button>
              </DialogClose>
              <Button
                type="submit"
                size="sm"
                className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                onClick={handlePaid}
                disabled={isPending}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                <span>
                  {isPending ? t("common.processing") : t("invoices.setToPaid")}
                </span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default InvoiceActions;
